import React from "react";
// UI Framework
import { toast } from "react-toastify";
// Common Components
import { TextInput, Button } from "src/Components";
import SelectableButtons from "src/Components/Dropdown/SelectableButtons";
// Hooks
import { useAppSelector } from "src/App/hooks";
// Types
import { IMinProfit } from "src/Interfaces/coveredCall";
import { DropdownItem } from "src/Interfaces/dropdownInterfaces";
// Requests
import {
  copyFixedBasePriceExpectedReturnSettings,
  getPairType2,
  getMinProfit,
  setMinProfit,
} from "src/Requests/coveredCall";
// Utilities
import { packetErrorHandler } from "src/Utils/requestUtils";

interface IProps {
  onClose: () => void;
  useFixedBasePrices?: boolean;
  initialRiskProfileId?: string;
  onCopied?: () => void;
}
const ExpectedReturnModal: React.FC<IProps> = ({
  onClose,
  useFixedBasePrices = false,
  initialRiskProfileId,
  onCopied,
}) => {
  // store
  const riskProfiles = useAppSelector(
    (store) => store.coveredCall.riskProfiles,
  );
  // local store
  const [config, setConfig] = React.useState<IMinProfit>({
    "1": 0,
    "2": 0,
    "3": 0,
    "4": 0,
    "5": 0,
    "6": 0,
    "7": 0,
    "8": 0,
  });
  const [pairType, setPairType] = React.useState<DropdownItem[]>([]);
  const [loading, setLoading] = React.useState(false);
  const [copying, setCopying] = React.useState(false);
  const [riskProfileId, setRiskProfileIds] = React.useState<string>(
    initialRiskProfileId ?? riskProfiles[0]?.id?.toString() ?? "0",
  );

  React.useEffect(() => {
    getPairType2()
      .then((res) => {
        setPairType(
          useFixedBasePrices
            ? res.filter((item) => Number(item.id) === 7)
            : res,
        );
      })
      .catch(packetErrorHandler);
  }, [useFixedBasePrices]);

  React.useEffect(() => {
    setLoading(true);
    getMinProfit(riskProfileId, useFixedBasePrices)
      .then((res) => {
        const newConfig = Object.assign({}, ...res);
        setConfig(newConfig);
      })
      .catch(packetErrorHandler)
      .finally(() => setLoading(false));
  }, [riskProfileId, useFixedBasePrices]);

  const changeHandler = (name: string, value: any) => {
    setConfig((prev) => ({
      ...prev,
      [name as `${number}`]: value ?? "",
    }));
  };

  const setConfigHandler = () => {
    if (useFixedBasePrices && config["7"] === undefined) {
      toast.error("بازده انتظاری کاوردکال بارگذاری نشده است");
      return;
    }

    const configEntries = useFixedBasePrices
      ? [["7", config["7"]] as const]
      : Object.entries(config);
    const newConfig = configEntries.map(([key, value]) => ({
      [key]: value,
    }));
    setLoading(true);
    setMinProfit(riskProfileId, newConfig, useFixedBasePrices)
      .then((resp) => {
        if (resp.data) {
          toast.success(resp.msg);
          onClose();
        } else {
          toast.error(resp.msg);
        }
      })
      .catch(packetErrorHandler)
      .finally(() => setLoading(false));
  };

  const copyFromEntryPosition = () => {
    const numericRiskProfileId = Number(riskProfileId);
    if (
      riskProfileId == null ||
      riskProfileId === "" ||
      !Number.isInteger(numericRiskProfileId) ||
      numericRiskProfileId < 0
    ) {
      toast.error("ریسک پروفایل انتخاب نشده است");
      return;
    }

    setCopying(true);
    copyFixedBasePriceExpectedReturnSettings(riskProfileId)
      .then(async (resp) => {
        if (!resp.data) {
          toast.error(resp.msg);
          return;
        }

        const copiedConfig = await getMinProfit(riskProfileId, true);
        setConfig(Object.assign({}, ...copiedConfig));
        toast.success(resp.msg);
        onCopied?.();
      })
      .catch(packetErrorHandler)
      .finally(() => setCopying(false));
  };

  return (
    <div className="w-full flex flex-col justify-center items-center gap-3">
      <SelectableButtons
        label="ریسک پروفایل"
        name="riskProfileId"
        onChange={(_: string, value: string) => setRiskProfileIds(value)}
        options={riskProfiles}
        value={riskProfileId}
        hideSelectAll
      />
      {pairType.map((item) => {
        const key = item.id.toString() as `${number}`;
        const value = config[key];
        return (
          <div key={key} className="flex w-full items-center">
            <span className="text-sm text-gray-500 w-64">% {item.name}</span>
            <TextInput
              label=""
              name={item.id.toString()}
              onChange={changeHandler}
              value={value == null ? "" : String(value)}
              kind="negativeDecimalNumbers"
              extraLabel
            />
          </div>
        );
      })}

      <div className="mt-7 flex w-full justify-center gap-3">
        {useFixedBasePrices ? (
          <div className="w-[200px]">
            <Button
              onClick={copyFromEntryPosition}
              text="کپی از ورود به پوزیشن"
              variant="outlined"
              loading={copying}
              disabled={loading || copying}
            />
          </div>
        ) : null}
        <div className="w-[200px]">
          <Button
            onClick={setConfigHandler}
            text="ثبت"
            variant="contained"
            loading={loading}
            disabled={loading || copying}
          />
        </div>
      </div>
    </div>
  );
};

export default ExpectedReturnModal;
